import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useSideNav } from '../context/SideNavContext';
import PageHeader from '../components/PageHeader';
import Card from '../components/Card';
import StatCard from '../components/StatCard';
import { Loading, ErrorState, EmptyState } from '../components/States';
import { number, relativeTime } from '../lib/format';
import { providerNav } from '../lib/providerNav';

// Version history behind the public price list: one row per past sync, and —
// for whichever one is selected — every SKU band that sync added, repriced, or
// withdrew compared with whatever was live immediately before it.
//
// The comparison is made once, at sync time (services.SyncSkuCatalogue), and
// stored rather than recomputed here: oci_sku_prices only ever holds the
// current catalogue, so the old version only still exists in what that sync
// wrote to its own change log.

const priceText = (v) =>
  v == null ? '—' : (v ?? 0).toLocaleString('en-US', { maximumFractionDigits: 8 });

const volumeText = (v) => {
  if (!v) return 'first';
  if (v >= 1e9) return `${Number((v / 1e9).toFixed(2))}B+`;
  if (v >= 1e6) return `${Number((v / 1e6).toFixed(2))}M+`;
  if (v >= 1e3) return `${Number((v / 1e3).toFixed(2))}K+`;
  return `${number(v)}+`;
};

const KIND_BADGE = { added: 'badge--ok', changed: 'badge--warn', removed: 'badge--danger' };

export default function ProviderPriceSyncHistory() {
  const { id } = useParams();
  const { setNav } = useSideNav();

  const providerReq = useAsync(() => api.getProvider(id), [id]);
  const provider = providerReq.data;

  useEffect(() => {
    if (provider) setNav(providerNav(provider));
  }, [provider, setNav]);
  useEffect(() => () => setNav(null), [setNav]);

  const syncsReq = useAsync(() => api.getSkuPriceSyncs(id), [id]);
  const syncs = useMemo(() => syncsReq.data?.data || [], [syncsReq.data]);

  const [selected, setSelected] = useState(null);
  const changesReq = useAsync(
    () => (selected ? api.getSkuPriceSyncChanges(id, selected) : Promise.resolve(null)),
    [id, selected]
  );
  const changes = useMemo(() => changesReq.data?.data || [], [changesReq.data]);
  const selectedSync = syncs.find((s) => s.id === selected);

  const totals = useMemo(
    () =>
      syncs.reduce(
        (t, s) => ({
          added: t.added + (s.added || 0),
          changed: t.changed + (s.changed || 0),
          removed: t.removed + (s.removed || 0),
        }),
        { added: 0, changed: 0, removed: 0 }
      ),
    [syncs]
  );

  if (providerReq.loading) return <Loading label="Loading provider…" />;
  if (providerReq.error) {
    return <ErrorState error={providerReq.error} onRetry={providerReq.reload} />;
  }

  return (
    <div className="stack">
      <PageHeader
        title="Price sync history"
        subtitle={`${provider?.name || id} — every past catalogue sync and what it changed`}
      />

      <div className="grid grid--kpi">
        <StatCard label="Syncs recorded" value={number(syncs.length)} />
        <StatCard
          label="Last synced"
          value={syncs[0] ? relativeTime(syncs[0].syncedAt) : '—'}
          hint={syncs[0]?.lastUpdated ? `catalogue published ${new Date(syncs[0].lastUpdated).toLocaleDateString()}` : undefined}
        />
        <StatCard label="Bands added" value={number(totals.added)} hint="across all syncs" />
        <StatCard
          label="Repriced / removed"
          value={number(totals.changed + totals.removed)}
          hint={`${number(totals.changed)} repriced · ${number(totals.removed)} removed`}
        />
      </div>

      <Card title="Syncs">
        {syncsReq.loading ? (
          <Loading label="Loading sync history…" />
        ) : syncsReq.error ? (
          <ErrorState error={syncsReq.error} onRetry={syncsReq.reload} />
        ) : syncs.length === 0 ? (
          <EmptyState
            title="No syncs recorded yet"
            detail="Sync the public price list from its page, and every run from here on is recorded for comparison."
          />
        ) : (
          <div className="table-wrap">
            <table className="table table--dense">
              <thead>
                <tr>
                  <th>Synced</th>
                  <th>Catalogue published</th>
                  <th className="num">SKUs</th>
                  <th className="num">Prices</th>
                  <th className="num">Added</th>
                  <th className="num">Repriced</th>
                  <th className="num">Removed</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {syncs.map((s) => (
                  <tr key={s.id} className={s.id === selected ? 'row--selected' : undefined}>
                    <td className="cell-primary">{new Date(s.syncedAt).toLocaleString()}</td>
                    <td>{s.lastUpdated ? new Date(s.lastUpdated).toLocaleDateString() : '—'}</td>
                    <td className="num">{number(s.skus)}</td>
                    <td className="num">{number(s.prices)}</td>
                    <td className="num">{s.added ? number(s.added) : '—'}</td>
                    <td className="num">{s.changed ? number(s.changed) : '—'}</td>
                    <td className="num">{s.removed ? number(s.removed) : '—'}</td>
                    <td className="num">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={!s.added && !s.changed && !s.removed}
                        onClick={() => setSelected(s.id === selected ? null : s.id)}
                      >
                        {s.id === selected ? 'Hide changes' : 'View changes'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {selected ? (
        <Card
          className="card--quiet-title"
          title={`Changes — ${selectedSync ? new Date(selectedSync.syncedAt).toLocaleString() : ''}`}
        >
          {changesReq.loading ? (
            <Loading label="Loading changes…" />
          ) : changesReq.error ? (
            <ErrorState error={changesReq.error} onRetry={changesReq.reload} />
          ) : changes.length === 0 ? (
            <EmptyState title="No changes" detail="This sync wrote the catalogue with nothing different from the version before it." />
          ) : (
            <div className="table-wrap">
              <table className="table table--dense">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Name</th>
                    <th>Currency</th>
                    <th>Band</th>
                    <th></th>
                    <th className="num">Previous rate</th>
                    <th className="num">New rate</th>
                  </tr>
                </thead>
                <tbody>
                  {changes.map((c, i) => (
                    <tr key={`${c.skuId}-${c.currency}-${c.rangeMin}-${i}`}>
                      <td className="cell-primary mono">{c.skuId}</td>
                      <td>{c.skuName || '—'}</td>
                      <td>{c.currency}</td>
                      <td>{volumeText(c.rangeMin)}</td>
                      <td>
                        <span className={`badge ${KIND_BADGE[c.kind] || 'badge--muted'} table__tag`}>
                          {c.kind}
                        </span>
                      </td>
                      <td className="num">{c.kind === 'added' ? '—' : priceText(c.oldPrice)}</td>
                      <td className="num">{c.kind === 'removed' ? '—' : priceText(c.newPrice)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : null}

      <p className="field__hint">
        Compared against the OCI public price list API only — not against any
        group's billing price. A SKU a group has priced by hand is unaffected
        either way: see Price management for the sell side.
      </p>
    </div>
  );
}
